import type { MouseEvent } from 'react';

import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import Button from '../../../../../components/buttons/Button';
import { CoinWithValue } from '../../../../../components/CoinWithValue/CoinWithValue';
import { useStores } from '../../../../../stores/useStores';
import { WidgetCrownBadge, WidgetWordmark } from '../JackpotWidgetArt';

import styles from './JackpotWideWidget.module.scss';

/**
 * FE_DESIGN_SPEC §7.4 variant B — the promo card: crown art, the `CROWN JACKPOT` wordmark, the coin
 * + the top level pot (the biggest prize on the ladder, by association priority), and a
 * state-driven CTA.
 *
 * The CTA label follows `participate` — `OPT IN` while the player is out, `VIEW` once they are in
 * (frames `17654:64648` and `14538:86806`). It stays the SAME green button in both states: the two
 * frames are identical but for that one word, so switching the button's colour on opt-in — which
 * this did — invents a state the design does not have.
 *
 * The tap follows the label. `OPT IN` joins in place — one tap, no popup — and `VIEW` opens the
 * popup as it always did. The card BODY still opens the popup in both states, so the levels, the
 * contribution cost and How-It-Works stay one tap away for a player who wants to read before joining.
 *
 * ## Three states, the same ones the in-slot banner shows
 * `OPT IN` -> `JOINING` (greyed, held) -> `VIEW`. The write takes two round trips — the POST and the
 * eligibility re-read the label follows — and a button that stays green and tappable through both
 * reads as a tap that did nothing. `disabled` is what greys it (see `Button`: it swaps `btnType` for
 * `gray`), and it is the store's `optInPending` that decides, so the widget cannot disagree with the
 * call actually in flight.
 */
function JackpotWideWidgetComponent() {
  const { t } = useTranslation();
  const { coinTypeStore, gameUiStore, jackpotStore } = useStores();

  const { showOptInCta, topLevelAmountLabelWide, optInPending, widgetThemeForSelectedCoin } = jackpotStore;

  const handleCtaClick = (event: MouseEvent<HTMLButtonElement>) => {
    // The rail wrapper's tap opens the popup too — without this the one tap both joins and opens it.
    event.stopPropagation();

    // `OPT IN` does what it says. The store reports the click, re-reads eligibility so the label
    // follows the server, and falls back to the popup if the write fails.
    if (showOptInCta) {
      void jackpotStore.optIn();

      return;
    }

    gameUiStore.openJackpotDialog({ source: 'cta' });
  };

  return (
    // The seasonal background is inline only once the store has decoded it, so a broken image never
    // paints; the SCSS keeps the bundled fill underneath and sizes whatever lands here (KRA-427).
    <div
      className={styles.container}
      data-testid="jackpot-widget-card"
      style={
        widgetThemeForSelectedCoin
          ? { backgroundImage: `url("${widgetThemeForSelectedCoin.backgroundUrl}")` }
          : undefined
      }
    >
      <div className={styles.art} data-testid="jackpot-widget-art">
        <WidgetCrownBadge className={styles.badge} />
      </div>

      <div className={styles.wrapper}>
        <WidgetWordmark alt={t('jackpot.wordmark')} className={styles.wordmark} />

        <CoinWithValue
          className={styles.amount}
          coinSide="left"
          coinType={coinTypeStore.coinType}
          // Breakpoint-aware coin size: the SCSS sets the variable, this beats the component's own
          // `1em` rule by being an inline style rather than by stylesheet order.
          coinWidth="var(--jp-widget-coin, 1em)"
          dataTestId="jackpot-widget-amount"
          valueClassName={styles.amountValue}
        >
          {topLevelAmountLabelWide}
        </CoinWithValue>

        <div className={styles.btnWrapper}>
          <Button
            btnType="main"
            className={styles.btn}
            dataTestId="jackpot-widget-cta"
            // Held for the whole round trip: a second tap while the first is in flight is never intent.
            disabled={optInPending}
            handleOnClick={handleCtaClick}
            popupAnimation="none"
          >
            {optInPending && t('jackpot.optingIn')}
            {!optInPending && (showOptInCta ? t('jackpot.optIn') : t('jackpot.view'))}
          </Button>
        </div>
      </div>
    </div>
  );
}

export const JackpotWideWidget = observer(JackpotWideWidgetComponent);
